// Calls /api/transcribe with the multipart/form-data shape it expects:
// a single field named 'file' carrying the audio blob. Server responds
// with JSON { text } on success or { error } on failure.

const ENDPOINT = '/api/transcribe';
const RETRY_BACKOFF_MS = 1000;

export default class TranscriptionSource {
  async transcribe(audioBlob, options = {}) {
    const { signal } = options;
    if (!(audioBlob instanceof Blob)) {
      throw new Error('TranscriptionSource.transcribe expects a Blob');
    }

    let response;
    try {
      response = await this._postOnce(audioBlob, signal);
    } catch (err) {
      if (err && err.name === 'AbortError') throw err;
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
      if (signal && signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      response = await this._postOnce(audioBlob, signal);
    }

    if (!response.ok) {
      const snippet = await response
        .text()
        .then((t) => t.slice(0, 200))
        .catch(() => '');
      throw new Error(
        `TranscriptionSource: /api/transcribe returned ${response.status}: ${snippet}`
      );
    }

    const data = await response.json();
    if (typeof data.text !== 'string') {
      throw new Error(
        `TranscriptionSource: response missing 'text' field — got ${JSON.stringify(data).slice(0, 200)}`
      );
    }
    return data;
  }

  async _postOnce(audioBlob, signal) {
    const filename = this._filenameFor(audioBlob.type);
    const form = new FormData();
    form.append('file', audioBlob, filename);
    return fetch(ENDPOINT, {
      method: 'POST',
      body: form,
      signal,
    });
  }

  _filenameFor(mimeType) {
    if (!mimeType) return 'recording.webm';
    if (mimeType.includes('mp4')) return 'recording.mp4';
    if (mimeType.includes('mpeg')) return 'recording.mp3';
    if (mimeType.includes('ogg')) return 'recording.ogg';
    if (mimeType.includes('wav')) return 'recording.wav';
    return 'recording.webm';
  }
}
